import { useCallback, useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { CAT_FIELDS, pick, txt } from "@/lib/format";
import { imgSrc } from "@/lib/media";
import { CATEGORY_ICONS } from "@/lib/icons";
import { useAdminLang } from "./context";
import { SortableList, SortableRow, moveId } from "./Sortable";
import { TrStatus } from "./Bilingual";
import CategoryEditor from "./CategoryEditor";
import DeleteCategoryDialog from "./DeleteCategoryDialog";

const CatRow = ({ c, handle, idx, total, t, lang, onToggle, onMove, onEdit, onDelete }) => {
  const name = pick(c, "name", lang);
  const Icon = CATEGORY_ICONS[c.icon];
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3 sm:flex-nowrap ${c.is_visible ? "" : "opacity-60"}`} data-testid={`admin-category-row-${c.id}`}>
      <button type="button" {...handle} aria-label="Drag" className="cursor-grab touch-none text-muted-foreground" data-testid={`category-drag-${c.id}`}><GripVertical className="h-5 w-5" /></button>
      <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-secondary text-primary">
        {c.image_url ? <img src={imgSrc(c.image_url, "sm")} alt="" className="h-full w-full object-cover" /> : Icon ? <Icon className="h-5 w-5" /> : null}
      </div>
      <div className="min-w-0 flex-1 basis-40 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => onEdit(c)} lang={name.lang} className="truncate font-semibold hover:text-primary" data-testid={`category-name-${c.id}`}>{name.text}</button>
          <span className="text-xs text-muted-foreground">{t("cat.items", { n: c.item_count })}</span>
          {c.menu_name_en || c.menu_name_ar ? (
            <span className="rounded-full bg-primary/25 px-2 py-0.5 text-[10px] font-semibold text-foreground" data-testid={`category-menu-chip-${c.id}`}>{txt({ name_en: c.menu_name_en, name_ar: c.menu_name_ar }, "name", lang)}</span>
          ) : (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300" data-testid={`category-unassigned-${c.id}`}>{t("cat.unassigned")}</span>
          )}
          {c.is_sample && <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">{t("common.sample")}</span>}
        </div>
        <TrStatus rec={c} fields={CAT_FIELDS} t={t} testid={`category-tr-${c.id}`} />
      </div>
      <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground"><Switch checked={c.is_visible} onCheckedChange={(v) => onToggle(c, v)} data-testid={`category-visible-toggle-${c.id}`} />{t("items.visible")}</label>
      <div className="flex shrink-0 items-center">
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === 0} onClick={() => onMove(c, -1)} aria-label={t("items.moveUp")} data-testid={`category-move-up-${c.id}`}><ArrowUp className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === total - 1} onClick={() => onMove(c, 1)} aria-label={t("items.moveDown")} data-testid={`category-move-down-${c.id}`}><ArrowDown className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(c)} aria-label={t("items.edit")} data-testid={`category-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => onDelete(c)} aria-label={t("items.delete")} data-testid={`category-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export default function Categories() {
  const { t, lang } = useAdminLang();
  const { refresh } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(params.get("new") ? { key: Date.now(), cat: null } : null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/menus").then((r) => setMenus(r.data)).catch(() => {});
    return api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => toast.error(t("toast.loadFailed"))).finally(() => setLoading(false));
  }, [t]);
  useEffect(() => {
    load();
  }, [load]);

  const reorder = async (ids) => {
    setCats((prev) => ids.map((id) => prev.find((c) => c.id === id)));
    try {
      await api.put("/admin/categories/reorder", { ids });
      toast.success(t("toast.orderSaved"));
      refresh();
    } catch {
      toast.error(t("toast.saveFailed"));
      load();
    }
  };

  const onToggle = async (c, v) => {
    setCats((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_visible: v } : x)));
    try {
      const { id, item_count, sort_order, created_at, updated_at, is_sample, menu_name_en, menu_name_ar, ...body } = { ...c, is_visible: v };
      await api.put(`/admin/categories/${id}`, body);
      refresh();
    } catch {
      toast.error(t("toast.saveFailed"));
      load();
    }
  };

  const closeEditor = () => {
    setEditor(null);
    if (params.get("new")) setParams({}, { replace: true });
  };
  const ids = cats.map((c) => c.id);

  return (
    <div className="space-y-6" data-testid="admin-categories-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{t("cat.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("cat.subtitle")}</p>
        </div>
        <Button onClick={() => setEditor({ key: Date.now(), cat: null })} className="rounded-full" data-testid="add-category-btn"><Plus className="h-4 w-4" />{t("cat.add")}</Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("items.dragHint")}</p>
      {!loading && !cats.length && <p className="rounded-2xl border border-dashed p-10 text-center text-muted-foreground" data-testid="categories-empty">{t("cat.empty")}</p>}
      <SortableList ids={ids} onReorder={reorder}>
        <div className="space-y-2">
          {cats.map((c, idx) => (
            <SortableRow key={c.id} id={c.id}>
              {(handle) => <CatRow c={c} handle={handle} idx={idx} total={cats.length} t={t} lang={lang} onToggle={onToggle} onMove={(x, d) => { const n = moveId(ids, x.id, d); if (n) reorder(n); }} onEdit={(x) => setEditor({ key: Date.now(), cat: x })} onDelete={setDeleting} />}
            </SortableRow>
          ))}
        </div>
      </SortableList>
      {editor && <CategoryEditor key={editor.key} category={editor.cat} menus={menus} t={t} onClose={closeEditor} onSaved={() => { closeEditor(); load(); refresh(); }} />}
      {deleting && <DeleteCategoryDialog category={deleting} categories={cats} t={t} lang={lang} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); load(); refresh(); }} />}
    </div>
  );
}
